import { ServerStorage } from "@/constants/app-data";
import type { ProductHomeType } from "@/types/home-type";
import { currencyFormat } from "@/utils/format-currency";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function ProductHomeCard({
    product,
}: {
    product: ProductHomeType;
}) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language as "id" | "en" | "my";
    const getDiscountPercent = (orig: number, disc: number) => {
        return Math.round(((orig - disc) / orig) * 100);
    };

    return (
        <Link
            to={`/${lang}/shop/${product.slug}`}
            className="group bg-cream dark:bg-dark-2 rounded-2xl border-2 border-transparent hover:border-gold dark:hover:border-gold transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_48px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col">
            <div className="w-full aspect-square bg-surface dark:bg-dark-2 flex items-center justify-center relative transition-colors duration-300">
                <img
                    src={
                        product.img.startsWith("/assets")
                            ? product.img
                            : `${ServerStorage}${product.img}`
                    }
                    alt={product.title[lang]}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full bg-white dark:bg-dark shadow-inner transition-colors duration-300 object-cover"
                />
                {product.isBestSeller && (
                    <div className="absolute z-10 top-3 left-3 bg-gold-light hover:bg-gold dark:bg-gold dark:hover:bg-gold-light text-dark text-[7px] md:text-xs font-bold px-2 py-0.5 md:px-3 md:py-1 rounded-full uppercase tracking-wider">
                        {t("home.product.best_seller")}
                    </div>
                )}
                {/* Discount Badge Visual */}
                {product.disc && (
                    <div className="absolute z-10 top-3 right-3 bg-red-600/30 dark:bg-red-500/20 text-red-900 dark:text-red-700 border border-red-600/20 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider shadow-sm flex items-center gap-1 group-hover:bg-red-600 group-hover:text-white transition-colors duration-300">
                        -
                        {getDiscountPercent(
                            product.orig[lang],
                            product.disc[lang],
                        )}
                        %
                    </div>
                )}
                {/* Out of Stock Overlay */}
                {(!product.inStock || (product.stock !== null && product.stock <= 0)) && (
                    <div className="absolute inset-0 bg-black/60 z-20 flex items-center justify-center backdrop-blur-[1px]">
                        <div className="bg-dark/90 border border-gold/30 text-white px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider">
                            {t("product_detail.out_of_stock")}
                        </div>
                    </div>
                )}
            </div>
            <div className="bg-surface-2/50 dark:bg-surface-2/5 p-3 md:p-4 border-t border-gold/10 dark:border-gold/10 grow flex flex-col justify-between">
                <div>
                    <h3 className="font-serif text-base line-clamp-1 md:text-lg text-dark dark:text-white mb-1.5 font-bold leading-tight">
                        {product.title[lang]}
                    </h3>
                </div>
                <div className="flex justify-between items-end">
                    <div className="flex flex-col">
                        {product.disc ? (
                            <>
                                {product.disc[lang] < product.orig[lang] && (
                                    <span className="text-gold dark:text-gray-400 text-xs line-through decoration-red-500/70">
                                        {currencyFormat(
                                            product.orig[lang],
                                            lang,
                                        )}
                                    </span>
                                )}

                                <span className="text-gold dark:text-gold-light font-bold text-base md:text-lg">
                                    {currencyFormat(product.disc[lang], lang)}
                                </span>
                            </>
                        ) : (
                            <span className="text-gold dark:text-gold-light font-bold text-base md:text-lg">
                                {currencyFormat(product.orig[lang], lang)}
                            </span>
                        )}
                    </div>
                    <div className="text-gold/50 group-hover:text-gold dark:text-gold-light/50 dark:group-hover:text-gold-light transition-all pb-1 pr-1 group-hover:translate-x-1 duration-300">
                        <ArrowRight size={20} strokeWidth={2.5} />
                    </div>
                </div>
            </div>
        </Link>
    );
}
